import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Video, Wand2, Library, Scissors, User2, Film, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import CreditsBadge from '../../components/video-studio/CreditsBadge';
import PageHeader from '../../components/ui/PageHeader';
import { useAiCredits } from '../../contexts/AiCreditsContext';
import SeedanceGenerator from '../../components/video-studio/SeedanceGenerator';
import HeygenGenerator from '../../components/video-studio/HeygenGenerator';
import VideoGallery from '../../components/video-studio/VideoGallery';
import TimelineEditor, { TimelineEditorHandle } from '../../components/video-studio/TimelineEditor';
import { videoStudioService } from '../../services/videoStudioService';
import {
  GeneratedVideo,
  VideoCreditBalance,
  ProviderStatus,
  VideoScene
} from '../../types/videoStudio';

type Tab = 'create' | 'gallery' | 'edit';
type CreateProvider = 'seedance' | 'heygen';

const VideoStudioPage: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('create');
  const [createProvider, setCreateProvider] = useState<CreateProvider>('seedance');
  const [balance, setBalance] = useState<VideoCreditBalance | null>(null);
  const [providers, setProviders] = useState<ProviderStatus | null>(null);
  // Vídeo usa os créditos de IA: comprar abre a recarga de IA, por cima da tela
  const { openRecharge: setCreditsOpenAi, summary: aiSummary } = useAiCredits();
  const setCreditsOpen = (abrir: boolean) => { if (abrir) setCreditsOpenAi(); };
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0);
  const [scenes, setScenes] = useState<VideoScene[]>([]);
  const editorRef = useRef<TimelineEditorHandle>(null);
  // Persist aspect ratio across sessions — easy for users to forget switching
  // back to vertical/horizontal between projects.
  const [aspectRatio, setAspectRatioState] = useState<string>(() => {
    try { return localStorage.getItem('vs_aspect_ratio') || '16:9'; } catch { return '16:9'; }
  });
  const setAspectRatio = useCallback((ratio: string) => {
    setAspectRatioState(ratio);
    try { localStorage.setItem('vs_aspect_ratio', ratio); } catch { /* quota / private mode */ }
  }, []);

  const loadBalance = useCallback(async () => {
    try {
      const r = await videoStudioService.getCredits();
      if (r.success && r.data) setBalance(r.data);
    } catch { /* silent */ }
  }, []);

  // Recarga de IA entrou (ou gasto em outra tela): o saldo do estúdio acompanha
  useEffect(() => { if (aiSummary) loadBalance(); }, [aiSummary?.available_cents, loadBalance]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadBalance();
    videoStudioService.providerStatus().then(r => {
      if (r.success && r.data) {
        setProviders(r.data);
        if (!r.data.seedance.configured && r.data.heygen.configured) {
          setCreateProvider('heygen');
        }
      }
    });
  }, [loadBalance]);

  // Stripe Checkout return: when the URL has ?vs_credit_session=cs_xxx,
  // confirm the session with the backend (idempotent) and refresh the balance.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('vs_credit_session');
    const cancelled = params.get('vs_credit_cancelled');

    if (cancelled) {
      toast('Compra cancelada', { icon: 'ℹ️' });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (!sessionId) return;

    // Strip the param immediately so a refresh doesn't re-trigger / spam confirms
    window.history.replaceState({}, '', window.location.pathname);
    toast.loading('Confirmando pagamento…', { id: 'vs-credit-confirm' });
    videoStudioService.confirmCreditCheckout(sessionId)
      .then((r) => {
        toast.dismiss('vs-credit-confirm');
        if (r.success && r.data) {
          setBalance(r.data.balance);
          if (r.data.already_credited) {
            toast.success('Créditos já estavam confirmados — saldo atualizado');
          } else {
            toast.success('Créditos adicionados com sucesso!');
          }
          setCreditsOpen(true);
        }
      })
      .catch((e) => {
        toast.dismiss('vs-credit-confirm');
        toast.error(e.response?.data?.message || 'Falha ao confirmar pagamento — entre em contato com o suporte se o valor foi cobrado');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVideoGenerated = (video: GeneratedVideo) => {
    setGalleryRefreshKey(k => k + 1);
    toast.success(t('videoStudio.scenes.successAdded'));
  };

  const addToTimeline = (video: GeneratedVideo) => {
    if (!video.original_url) {
      toast.error(t('videoStudio.gallery.notReady'));
      return;
    }
    // Vai direto pro editor montado (que roteia áudio/imagem/vídeo pra faixa
    // certa). Antes isso empilhava uma "scene" e a remontagem do editor
    // descartava todo o resto da timeline.
    editorRef.current?.addAsset(video);
    setTab('edit');
  };

  const tabConfig: { value: Tab; label: string; icon: any; description: string }[] = [
    { value: 'create', label: t('videoStudio.tabs.create'), icon: Wand2, description: t('videoStudio.tabs.createDesc') },
    { value: 'gallery', label: t('videoStudio.tabs.gallery'), icon: Library, description: t('videoStudio.tabs.galleryDesc') },
    { value: 'edit', label: `${t('videoStudio.tabs.editor')}${scenes.length ? ` (${scenes.length})` : ''}`, icon: Scissors, description: t('videoStudio.tabs.editorDesc') }
  ];

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Cabeçalho padrão: título + explicação + créditos (toque para comprar) */}
      <PageHeader
        icon={Video}
        title={<span className="inline-flex items-center gap-2">{t('videoStudio.title')}<span className="px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-[11px] font-semibold align-middle">Beta</span></span>}
        description={t('videoStudio.subtitle')}
        actions={<CreditsBadge balance={balance} onBuy={() => setCreditsOpen(true)} compact />}
      />

      {/* Tabs */}
      {/* Celular: 3 abas lado a lado (ícone em cima do nome), sem descrição */}
      <div className="grid grid-cols-3 sm:flex gap-1.5 sm:gap-2 p-1.5 rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800">
        {tabConfig.map(tabItem => {
          const Icon = tabItem.icon;
          const active = tab === tabItem.value;
          return (
            <button
              key={tabItem.value}
              onClick={() => setTab(tabItem.value)}
              className={`flex-1 min-w-0 flex flex-col sm:flex-row items-center gap-1 sm:gap-3 p-2 sm:p-3 rounded-xl text-center sm:text-left transition ${
                active
                  ? 'bg-gradient-to-r from-primary-500/20 to-purple-500/20 border border-primary-500/40 text-primary-700 dark:text-white'
                  : 'text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent'
              }`}
            >
              <div className={`flex-shrink-0 p-1.5 sm:p-2 rounded-lg ${active ? 'bg-primary-500/20 text-primary-600 dark:text-primary-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 max-w-full">
                <p className="text-xs sm:text-sm font-semibold truncate">{tabItem.label}</p>
                <p className="hidden sm:block text-[11px] text-gray-500 dark:text-gray-500 truncate">{tabItem.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {tab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
          <div className="lg:col-span-1 space-y-3">
            <div className="rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 p-2 space-y-1">
              <button
                onClick={() => setCreateProvider('seedance')}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition text-left ${
                  createProvider === 'seedance'
                    ? 'bg-cyan-500/10 border border-cyan-500/30 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent'
                }`}
              >
                <div className={`flex-shrink-0 p-2 rounded-lg ${createProvider === 'seedance' ? 'bg-cyan-500/20 text-cyan-300' : 'bg-gray-800 text-gray-500'}`}>
                  <Film className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                    <p className="text-sm font-semibold">{t('videoStudio.providers.scenesAi')}</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30">
                      Seedance 2.5
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500">{t('videoStudio.providers.scenesAiDesc')}</p>
                </div>
              </button>
              <button
                onClick={() => setCreateProvider('heygen')}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition text-left ${
                  createProvider === 'heygen'
                    ? 'bg-violet-500/10 border border-violet-500/30 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent'
                }`}
              >
                <div className={`flex-shrink-0 p-2 rounded-lg ${createProvider === 'heygen' ? 'bg-violet-500/20 text-violet-300' : 'bg-gray-800 text-gray-500'}`}>
                  <User2 className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{t('videoStudio.providers.presenter')}</p>
                  <p className="text-[11px] text-gray-500">{t('videoStudio.providers.presenterDesc')}</p>
                </div>
              </button>
            </div>

            <div className="rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 p-4 text-xs text-gray-600 dark:text-gray-400 space-y-2">
              <p className="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-primary-400" />
                {t('videoStudio.creditsExplain.title', 'Como funcionam os créditos')}
              </p>
              <p>{t('videoStudio.creditsExplain.line1Ai', 'Vídeo usa os mesmos créditos de IA do resto da plataforma (1 crédito = R$ 0,01). Cenas IA: 210 a 1.120 créditos por segundo conforme o modelo e a resolução (Seedance 2.5 em 720p = 490). Apresentadores IA: ~1.750 créditos por segmento de 30s.')}</p>
              <p>{t('videoStudio.creditsExplain.line2', 'Vídeos que falham são reembolsados automaticamente.')}</p>
              <button
                onClick={() => setCreditsOpen(true)}
                className="w-full mt-2 py-2 rounded-lg bg-white/5 text-gray-900 dark:text-white text-xs font-semibold hover:bg-gray-200 dark:hover:bg-white/10 transition"
              >
                {t('videoStudio.creditsExplain.recharge', 'Recarregar créditos de IA')}
              </button>
            </div>
          </div>

          <div className="lg:col-span-2 min-w-0 rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 p-3 sm:p-6">
            {createProvider === 'seedance' ? (
              <SeedanceGenerator
                onGenerated={handleVideoGenerated}
                balance={balance}
                onBalanceRefresh={loadBalance}
                onBuyCredits={() => setCreditsOpen(true)}
              />
            ) : (
              <HeygenGenerator
                configured={providers?.heygen.configured ?? false}
                onGenerated={handleVideoGenerated}
                balance={balance}
                onBalanceRefresh={loadBalance}
                onBuyCredits={() => setCreditsOpen(true)}
              />
            )}
          </div>
        </div>
      )}

      {tab === 'gallery' && (
        <div className="rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 p-3 sm:p-5">
          <VideoGallery
            refreshKey={galleryRefreshKey}
            onAddToTimeline={addToTimeline}
            onCreate={() => setTab('create')}
          />
        </div>
      )}

      {/* O editor fica SEMPRE montado (só escondido) — desmontar perdia a
          timeline inteira (áudio, textos, legendas, trims) ao visitar outra aba. */}
      <div className={tab === 'edit' ? '' : 'hidden'}>
        <div className="rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 p-3 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">{t('videoStudio.editorBox.title', 'Editor de vídeo')}</p>
              <p className="text-sm text-gray-500">{t('videoStudio.editorBox.desc', 'Junte as cenas, coloque textos e baixe um vídeo só')}</p>
            </div>
            <select
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500/50"
            >
              <option value="16:9">{t('videoStudio.ratio.wide', 'Deitado 16:9 (YouTube)')}</option>
              <option value="9:16">{t('videoStudio.ratio.vertical', 'Em pé 9:16 (Reels, Stories)')}</option>
              <option value="1:1">{t('videoStudio.ratio.square', 'Quadrado 1:1 (feed)')}</option>
              <option value="4:3">{t('videoStudio.ratio.classic', 'Clássico 4:3')}</option>
            </select>
          </div>
          <TimelineEditor
            ref={editorRef}
            scenes={scenes}
            onChange={setScenes}
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
            serverRenderAvailable={providers?.server_render?.available ?? false}
            onExported={(v) => {
              // Não limpa a timeline: o usuário costuma querer ajustar e
              // reexportar, e no render do servidor ele pode nem estar aqui.
              setGalleryRefreshKey(k => k + 1);
              setTab('gallery');
              loadBalance();
            }}
          />
        </div>
      </div>

    </div>
  );
};

export default VideoStudioPage;
